import {
  HttpClient,
  HttpDownloadProgressEvent,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { getDocPrepServiceDomain } from '@et/utils';
import {
  filter,
  from,
  map,
  Observable,
  scan,
  switchMap,
  takeWhile,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AutotagProcessingService {
  private http = inject(HttpClient);

  /**
   * Subscribes to server-sent events for a document and emits parsed progress updates.
   *
   * Connects to the doc-prep SSE endpoint for the given externalId using an HTTP GET with
   * Accept: text/event-stream. The response stream is observed as download progress events;
   * partialText chunks are accumulated into a buffer and split on SSE message boundaries ("\n\n").
   * Each "data:" line is parsed as JSON with shape { status: string; progress: number } and emitted
   * as { progress, status }.
   *
   * Emissions continue until a message with status === 'done' is received (inclusive). JSON parse
   * errors are logged and do not terminate the stream. HTTP/network errors propagate to the subscriber.
   *
   * @param {string} externalId - External document ID to watch for status updates.
   * @returns {Observable<{ progress: number; status: string }>} Observable emitting parsed progress/status updates.
   */
  getStatusUpdates(
    externalId: string,
  ): Observable<{ progress: number; status: string }> {
    const url = `${getDocPrepServiceDomain()}/api/document/${externalId}/status-external/event`;

    const headers = new HttpHeaders({
      Accept: 'text/event-stream',
    });

    return this.http
      .get(url, {
        headers,
        reportProgress: true,
        observe: 'events',
        responseType: 'text',
      })
      .pipe(
        filter(
          (event: HttpEvent<string>): event is HttpDownloadProgressEvent =>
            event.type === HttpEventType.DownloadProgress,
        ),
        map((event) => event.partialText || ''),
        scan(
          (
            acc: {
              buffer: string;
              processedLength: number;
              messages: { progress: number; status: string }[];
            },
            partialText,
          ) => {
            const newChunk = partialText.slice(acc.processedLength);
            acc.buffer += newChunk;
            acc.processedLength = partialText.length;

            const lines = acc.buffer.split('\n\n');
            acc.buffer = lines.pop() || '';

            acc.messages = [];
            lines.forEach((line) => {
              const trimmed = line.trim();
              if (trimmed.startsWith('data:')) {
                const dataStr = trimmed.substring(5).trim();
                try {
                  const parsedData = JSON.parse(dataStr) as {
                    status: string;
                    progress: number;
                  };
                  acc.messages.push({
                    status: parsedData.status,
                    progress: parsedData.progress,
                  });
                } catch (error) {
                  console.error('Parse error:', error);
                }
              }
            });
            return acc;
          },
          {
            buffer: '',
            processedLength: 0,
            messages: [] as { progress: number; status: string }[],
          },
        ),
        switchMap((acc) => from(acc.messages)),
        takeWhile((data) => data.status !== 'done', true),
      );
  }
}
